import * as B from '@blueprintjs/core';
import React, {useState} from 'react';

import AgentContactDialog from 'app/components/AgentContactDialog';
import {usePushNotification} from 'app/components/Notification';
import {useUserInfo} from 'app/providers/AuthProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as C from 'app/utils/constants';
import featureFlags, {isUpstreamStaff} from 'app/utils/featureFlags';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {atLeastUserRole} from 'app/utils/userUtils';

import AgentPromptInput from '../AgentPromptInput';
import {CONTACT_DIALOG_COPY, ContactDialogVariant} from '../contactDialogCopy';
import {FewShotExemplarFlow, termsMatch} from '../fewShotExemplarTypes';
import {useAgentRunCount, useStartExploreRun} from '../hooks';
import {OptionCard} from '../OptionCard';
import RunsView from '../RunsView';
import ScenePreview from '../ScenePreview';
import {ApiExploreRun, ExploreRunAgentType, ExploreRunPrompt} from '../types';
import {UseCaseCard} from '../UseCaseCard';
import FewShotExemplarInput from './FewShotExemplarInput';
import {
  AGENT_CUSTOM_PROMPT_KEY,
  AGENT_CUSTOM_PROMPT_USE_CASE,
  AGENT_TYPE_OPTIONS,
  AGENT_USE_CASES,
  getAgentTypePlaceholder,
  getUnsupportedScenesMessage,
  imageRefsAreSupported,
} from './promptConfigs';
import cs from './PromptView.module.scss';

export type Tab = 'prompt' | 'history';

interface Props {
  onSelectRun: (run: ApiExploreRun) => void;
  featureId: number;
  imageRefs: mapUtils.MapImageRefs;
  activeTab: Tab;
  onActiveTabChange: (tab: Tab) => void;
  showHistoryTab: boolean;
  onEnterCompareMode: () => void;
  onExitCompareMode: () => void;
  fewShotFlow: FewShotExemplarFlow;
}

const PromptView: React.FC<Props> = ({
  onSelectRun,
  featureId,
  imageRefs,
  activeTab,
  onActiveTabChange,
  showHistoryTab,
  onEnterCompareMode,
  onExitCompareMode,
  fewShotFlow,
}) => {
  const [selectedUseCaseKey, setSelectedUseCaseKey] = useState<string | null>(null);
  const [customPromptText, setCustomPromptText] = useState('');
  // Defaults to describe, so the custom card behaves as it did before identify mode existed.
  const [customPromptAgentType, setCustomPromptAgentType] =
    useState<ExploreRunAgentType>('describe');
  const [contactDialog, setContactDialog] = useState<ContactDialogVariant | null>(null);

  // Upstream staff may run a custom free-text prompt in addition to the fixed use cases.
  const [organization, profile] = useUserInfo();
  const canUseCustomPrompt = isUpstreamStaff(profile);
  const canKickoffRuns = atLeastUserRole(C.USER_ROLE_REGULAR, profile);
  const canUseFewShot = featureFlags.AGENT_FEW_SHOT(organization, profile);

  const pushNotification = usePushNotification();
  const {fewShotState} = fewShotFlow;
  const isFewShotSelected = selectedUseCaseKey === C.AGENT_PROMPT_KEY_FIND_SIMILAR;

  // Exemplars are drawn on a specific image; if the map's image refs change underneath a
  // composed run, the boxes no longer mean anything, so drop them and say why.
  const prevImageRefsKeyRef = React.useRef(JSON.stringify(imageRefs));
  React.useEffect(() => {
    const key = JSON.stringify(imageRefs);
    const changed = key !== prevImageRefsKeyRef.current;
    prevImageRefsKeyRef.current = key;
    const hasBoxes = fewShotState.exemplars.length + fewShotState.negativeExemplars.length > 0;
    if (changed && hasBoxes) {
      fewShotFlow.clearExemplars();
      fewShotFlow.stopDrawingExemplar();
      pushNotification({
        message: 'Examples cleared: they were drawn on a different image',
        autoHideDuration: 3000,
        options: {intent: B.Intent.WARNING},
      });
    }
    // fewShotState and pushNotification are intentionally left out: this should only
    // react to imageRefs changing, not to the exemplars list changing as boxes are drawn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageRefs]);

  // The pill shows runs remaining: the org's quota minus its used (non-Upstream) run count.
  const agentSettings = organization.getIn(['settings', C.ORG_SETTING_AGENT_SETTINGS])?.toJS() as
    | {run_quota?: number}
    | undefined;
  const runQuota = agentSettings?.run_quota;
  const {data: runsUsed} = useAgentRunCount(organization.get('id'));
  const runsRemaining =
    runQuota !== undefined && runsUsed !== undefined ? Math.max(runQuota - runsUsed, 0) : undefined;
  // Upstream staff bypass the quota server-side.
  const isOutOfRuns = runsRemaining === 0 && !canUseCustomPrompt;
  const isPresetOnlyTrial = runQuota !== undefined && !canUseCustomPrompt;

  const scenes = imageRefs.map((imageRef) => {
    const {sourceId, layerId} = layerUtils.parseLayerKey(imageRef.layerKey);
    return {sourceId, layerId, sensingTime: imageRef.cursor!};
  });

  const {
    mutateAsync: startExploreRun,
    isPending: isStartingRun,
    error: startRunError,
  } = useStartExploreRun(featureId, scenes);

  const runPrompt = async (prompt: ExploreRunPrompt) => {
    try {
      const run = await startExploreRun(prompt);
      onSelectRun(run);
      recordEvent('Kicked off agent run', {
        runId: run.id,
        promptKey: prompt.promptKey || 'custom',
      });
      fewShotFlow.resetFewShot();
    } catch (error) {
      console.error(error);
    }
  };

  const scenesSupported = imageRefsAreSupported(imageRefs);
  const compareMode = imageRefs.length === 2;

  const isCustomSelected = selectedUseCaseKey === AGENT_CUSTOM_PROMPT_KEY;

  // The find-similar card is staff-only behind AGENT_FEW_SHOT; everything else stays visible to
  // everyone, so filter it out of the generic list rather than gating AGENT_USE_CASES itself
  // (title/analysis-phrase lookups for existing runs still need it there).
  const visibleUseCases = AGENT_USE_CASES.filter(
    (useCase) => useCase.key !== C.AGENT_PROMPT_KEY_FIND_SIMILAR || canUseFewShot
  );

  const firstInvalidExemplar = fewShotFlow.exemplarProblems.positive.findIndex((p) => p !== null);
  const firstInvalidNegative = fewShotFlow.exemplarProblems.negative.findIndex((p) => p !== null);
  // Why the few-shot run can't start yet, in the order the user should fix things.
  const fewShotBlocker = compareMode
    ? 'Draw examples on a single image'
    : fewShotState.exemplars.length === 0
      ? 'Draw at least one example.'
      : firstInvalidExemplar !== -1
        ? `Fix or remove example ${firstInvalidExemplar + 1}.`
        : firstInvalidNegative !== -1
          ? `Fix or remove ignore box ${firstInvalidNegative + 1}.`
          : fewShotState.label.trim().length === 0
            ? 'Say what your examples are.'
            : termsMatch(fewShotState)
              ? 'The ignore term matches the search term.'
              : null;
  const fewShotReady = fewShotBlocker === null;

  return (
    <div className={cs.promptLayout}>
      <div className={cs.header}>
        <div className={cs.headerTop}>
          <h4 className={cs.title}>
            <B.Icon icon="clean" size={20} className={cs.titleIcon} />
            Agent
          </h4>
          {runsRemaining !== undefined && runQuota !== undefined && (
            <button className={cs.runsPill} onClick={() => setContactDialog('quota')}>
              <B.Icon icon="clean" size={12} />
              {runsRemaining} of {runQuota} runs left
            </button>
          )}
        </div>
        <p className={cs.headerDescription}>
          Run a prompt on a property and the Agent will review your selected imagery, draft
          observations, and focus your attention where it matters.
        </p>
        <a
          href="https://lens.tech/support/article/lens-agent"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more
        </a>
      </div>

      {showHistoryTab && (
        <div className={cs.tabsBar}>
          <B.SegmentedControl
            fill
            value={activeTab}
            onValueChange={(value) => onActiveTabChange(value as Tab)}
            options={[
              {label: 'Prompt', value: 'prompt'},
              {label: 'History', value: 'history'},
            ]}
          />
        </div>
      )}

      {activeTab === 'prompt' && (
        <>
          <div className={cs.promptBody}>
            <div className={cs.sceneCard}>
              <ScenePreview
                featureId={featureId}
                imageRefs={imageRefs}
                onEnterCompareMode={onEnterCompareMode}
                onExitCompareMode={onExitCompareMode}
              />
            </div>

            <div className={cs.useCasesHeader}>
              <span className={cs.useCasesLabel}>Choose a prompt</span>
              {isPresetOnlyTrial && (
                <span className={cs.presetsOnlyBadge}>Free trial · Presets only</span>
              )}
            </div>
            <div className={cs.useCases}>
              {visibleUseCases.map((useCase) => (
                <UseCaseCard
                  key={useCase.key}
                  useCase={useCase}
                  compareMode={compareMode}
                  selected={selectedUseCaseKey === useCase.key}
                  disabled={isStartingRun}
                  onSelect={setSelectedUseCaseKey}
                />
              ))}
              <OptionCard
                dashed
                title="Custom prompt"
                description="Ask the Agent to look for something specific."
                tag="Talk to our team"
                onSelect={() => setContactDialog('custom-prompt')}
              />
              {canUseCustomPrompt && (
                <UseCaseCard
                  useCase={AGENT_CUSTOM_PROMPT_USE_CASE}
                  compareMode={compareMode}
                  selected={isCustomSelected}
                  disabled={isStartingRun}
                  onSelect={setSelectedUseCaseKey}
                />
              )}
            </div>

            {isCustomSelected && (
              <>
                <B.SegmentedControl
                  fill
                  value={customPromptAgentType}
                  onValueChange={(value) => setCustomPromptAgentType(value as ExploreRunAgentType)}
                  options={AGENT_TYPE_OPTIONS.map(({value, label}) => ({label, value}))}
                />
                {/* The custom prompt has its own submit (send) button, so no separate run button in the footer. */}
                <AgentPromptInput
                  value={customPromptText}
                  onChange={setCustomPromptText}
                  loading={isStartingRun}
                  onSubmit={() =>
                    runPrompt({userPrompt: customPromptText, agentType: customPromptAgentType})
                  }
                  imageRefs={imageRefs}
                  placeholder={getAgentTypePlaceholder(customPromptAgentType, compareMode)}
                />
              </>
            )}

            {isFewShotSelected && !compareMode && (
              <FewShotExemplarInput fewShotFlow={fewShotFlow} />
            )}
          </div>

          <div className={cs.footer}>
            {!isCustomSelected && (
              <B.Tooltip
                fill
                disabled={
                  scenesSupported &&
                  !!selectedUseCaseKey &&
                  !isStartingRun &&
                  !isOutOfRuns &&
                  canKickoffRuns &&
                  (!isFewShotSelected || fewShotReady)
                }
                content={[
                  isOutOfRuns && 'Your organization has no runs remaining.',
                  !scenesSupported && getUnsupportedScenesMessage(imageRefs),
                  !canKickoffRuns &&
                    'Your account is read-only. You can view completed runs in the History tab.',
                  isFewShotSelected && fewShotBlocker,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <B.Button
                  className={cs.runButton}
                  text={selectedUseCaseKey ? 'Run prompt' : 'Select a prompt to run'}
                  intent={selectedUseCaseKey ? 'primary' : 'none'}
                  fill
                  loading={isStartingRun}
                  disabled={
                    !selectedUseCaseKey ||
                    !scenesSupported ||
                    isStartingRun ||
                    isOutOfRuns ||
                    !canKickoffRuns ||
                    (isFewShotSelected && !fewShotReady)
                  }
                  onClick={() =>
                    selectedUseCaseKey &&
                    runPrompt({
                      promptKey: selectedUseCaseKey,
                      fewShot: isFewShotSelected
                        ? {
                            label: fewShotState.label.trim(),
                            exemplars: fewShotState.exemplars,
                            negativeExemplars: fewShotState.negativeExemplars,
                            searchTerm: fewShotState.searchTerm.trim(),
                            negativeLabel: fewShotState.negativeLabel.trim(),
                          }
                        : undefined,
                    })
                  }
                />
              </B.Tooltip>
            )}

            {runsRemaining !== undefined && (
              <p className={cs.runsLabel}>
                {isOutOfRuns ? (
                  <>
                    No runs remaining —{' '}
                    <button className={cs.quotaLink} onClick={() => setContactDialog('quota')}>
                      increase your quota
                    </button>
                  </>
                ) : (
                  `Uses 1 of your ${runsRemaining} remaining runs`
                )}
              </p>
            )}

            {startRunError && (
              <B.Callout intent="danger" icon="error">
                {(startRunError as Error).message || 'Failed to start agent run. Please try again.'}
              </B.Callout>
            )}
          </div>
        </>
      )}
      {activeTab === 'history' && (
        <div className={cs.historyBody}>
          <RunsView onSelectRun={onSelectRun} featureId={featureId} />
        </div>
      )}

      {contactDialog && (
        <AgentContactDialog
          isOpen
          onClose={() => setContactDialog(null)}
          {...CONTACT_DIALOG_COPY[contactDialog]}
        />
      )}
    </div>
  );
};

export default PromptView;
